import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  StarIcon, 
  MagnifyingGlassIcon, 
  FunnelIcon,
  FireIcon,
  ClockIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  AcademicCapIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  BookOpenIcon,
  SparklesIcon
} from '@heroicons/react/24/outline'
import { 
  StarIcon as StarSolidIcon,
  FireIcon as FireSolidIcon 
} from '@heroicons/react/24/solid'

interface Course {
  id: string
  title: string
  description: string
  subject: string
  price: string
  finalPricePerHour?: string
  duration: string
  status: string
  createdAt: string
  updatedAt?: string
  teacher: {
    id: string
    title: string
    rating: string
    user: {
      firstName: string
      lastName: string
    }
  }
  _count?: {
    bookings: number
  }
}

interface CourseStats {
  totalCourses: number
  averagePrice: number
  mostPopularSubject: string
  totalBookings: number
  newCoursesThisWeek: number
}

const CoursesPage = () => {
  const [courses, setCourses] = useState<Course[]>([])
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [sortBy, setSortBy] = useState('title')
  const [showFilters, setShowFilters] = useState(false)
  const [stats, setStats] = useState<CourseStats>({
    totalCourses: 0,
    averagePrice: 0,
    mostPopularSubject: '',
    totalBookings: 0,
    newCoursesThisWeek: 0
  })

  const subjects = [
    '数学', '英语', '物理', '化学', '生物', '历史', '地理', '编程', '音乐', '美术', '语文'
  ]

  useEffect(() => {
    fetchCourses()
  }, [])

  useEffect(() => {
    filterAndSortCourses()
    calculateStats()
  }, [courses, searchTerm, selectedCategory, sortBy])

  const fetchCourses = async () => {
    try {
      const response = await fetch('/api/courses')
      if (response.ok) {
        const result = await response.json()
        // 支持多种 API 响应格式
        const remoteCourses = result?.data?.courses || result?.courses || result?.data || result
        if (Array.isArray(remoteCourses)) {
          setCourses(remoteCourses)
        } else {
          console.warn('课程数据格式不正确:', result)
          setCourses([])
        }
      } else {
        console.error('获取课程失败:', response.status)
        setCourses([])
      }
    } catch (error) {
      console.error('获取课程失败:', error)
      setCourses([])
    } finally {
      setLoading(false)
    }
  }

  const calculateStats = () => {
    const approvedCourses = courses.filter(c => c.status === 'approved')
    const totalCourses = approvedCourses.length
    
    const averagePrice = totalCourses > 0 
      ? approvedCourses.reduce((sum, c) => sum + parseFloat(c.finalPricePerHour || c.price), 0) / totalCourses
      : 0
    
    // 计算最受欢迎的科目
    const subjectCounts = approvedCourses.reduce((acc, course) => {
      acc[course.subject] = (acc[course.subject] || 0) + (course._count?.bookings || 0)
      return acc
    }, {} as Record<string, number>)
    
    const mostPopularSubject = Object.entries(subjectCounts)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || ''
    
    const totalBookings = approvedCourses.reduce((sum, c) => sum + (c._count?.bookings || 0), 0)
    
    // 计算本周新课程
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    const newCoursesThisWeek = approvedCourses.filter(c => 
      new Date(c.createdAt) > oneWeekAgo
    ).length

    setStats({
      totalCourses,
      averagePrice,
      mostPopularSubject,
      totalBookings,
      newCoursesThisWeek
    })
  }

  const isNewCourse = (course: Course): boolean => {
    const courseTime = new Date(course.createdAt)
    const now = new Date()
    const timeDiff = now.getTime() - courseTime.getTime()
    const daysDiff = timeDiff / (1000 * 3600 * 24)
    return daysDiff <= 7 // 7天内的课程算新课程
  }

  const isPopularCourse = (course: Course): boolean => {
    return (course._count?.bookings || 0) >= 10 // 10个以上预约算热门
  }

  const isDiscountCourse = (course: Course): boolean => {
    return course.finalPricePerHour && course.price && 
           parseFloat(course.price) < parseFloat(course.finalPricePerHour)
  }

  const filterAndSortCourses = () => {
    let filtered = courses.filter(course => {
      const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          course.description.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesSubject = !selectedCategory || course.subject === selectedCategory
      const isApproved = course.status === 'approved'

      return matchesSearch && matchesSubject && isApproved
    })

    // 排序
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price':
          return parseFloat(a.price) - parseFloat(b.price)
        case 'price-desc':
          return parseFloat(b.price) - parseFloat(a.price)
        case 'duration':
          return parseFloat(a.duration) - parseFloat(b.duration)
        case 'popular':
          return (b._count?.bookings || 0) - (a._count?.bookings || 0)
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        default:
          return a.title.localeCompare(b.title)
      }
    })

    setFilteredCourses(filtered)
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedCategory('')
    setSortBy('title')
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:py-10 px-3 sm:px-6 lg:px-8">
      <div className="sm:px-0">
        {/* 页面标题和统计 */}
        <div className="mb-8 sm:mb-12">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <BookOpenIcon className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl sm:text-4xl font-bold text-gray-900">探索课程</h1>
          </div>
          <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 max-w-2xl">
            发现适合您的课程，开启您的学习之旅。每一门课程都由专业导师精心打造
          </p>
          
          {/* 统计卡片 - 移动端滚动显示 */}
          <div className="flex overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-5 gap-3 sm:gap-4 mb-4 sm:mb-6 scrollbar-hide">
            <div className="flex-shrink-0 w-[140px] sm:w-auto bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 p-4 sm:p-5 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-2">
                <BookOpenIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-xl sm:text-2xl font-bold text-blue-600">{stats.totalCourses}</div>
              <div className="text-xs text-blue-600/70 font-medium">总课程数</div>
            </div>
            
            <div className="flex-shrink-0 w-[140px] sm:w-auto bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-100 p-4 sm:p-5 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center mx-auto mb-2">
                <CurrencyDollarIcon className="w-5 h-5 text-green-600" />
              </div>
              <div className="text-xl sm:text-2xl font-bold text-green-600">¥{Math.round(stats.averagePrice)}</div>
              <div className="text-xs text-green-600/70 font-medium">平均价格</div>
            </div>
            
            <div className="flex-shrink-0 w-[140px] sm:w-auto bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl border border-purple-100 p-4 sm:p-5 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center mx-auto mb-2">
                <ArrowTrendingUpIcon className="w-5 h-5 text-purple-600" />
              </div>
              <div className="text-xl sm:text-2xl font-bold text-purple-600 truncate">{stats.mostPopularSubject || '暂无'}</div>
              <div className="text-xs text-purple-600/70 font-medium">热门科目</div>
            </div>
            
            <div className="flex-shrink-0 w-[140px] sm:w-auto bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl border border-orange-100 p-4 sm:p-5 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center mx-auto mb-2">
                <UserGroupIcon className="w-5 h-5 text-orange-600" />
              </div>
              <div className="text-xl sm:text-2xl font-bold text-orange-600">{stats.totalBookings}</div>
              <div className="text-xs text-orange-600/70 font-medium">总预约数</div>
            </div>
            
            <div className="flex-shrink-0 w-[140px] sm:w-auto bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl border border-pink-100 p-4 sm:p-5 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center mx-auto mb-2">
                <SparklesIcon className="w-5 h-5 text-pink-600" />
              </div>
              <div className="text-xl sm:text-2xl font-bold text-pink-600">{stats.newCoursesThisWeek}</div>
              <div className="text-xs text-pink-600/70 font-medium">本周新课程</div>
            </div>
          </div>
        </div>

        {/* 搜索和筛选区域 */}
        <div className="bg-white/80 backdrop-blur-xl shadow-xl rounded-2xl border border-gray-100 p-5 sm:p-8 mb-6 sm:mb-8">
          <div className="flex flex-col gap-3 sm:gap-4">
            {/* 搜索框 */}
            <div className="w-full">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg sm:rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="搜索课程..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* 筛选和排序 */}
            <div className="flex items-center gap-2 sm:gap-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex-shrink-0 inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 rounded-lg sm:rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FunnelIcon className="h-4 w-4 sm:mr-2" />
                <span className="hidden xs:inline">筛选</span>
              </button>

              {/* 排序 */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="flex-1 min-w-0 pl-3 pr-8 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-lg sm:rounded-md bg-white"
              >
                <option value="title">按名称</option>
                <option value="price">价格↑</option>
                <option value="price-desc">价格↓</option>
                <option value="duration">时长</option>
                <option value="popular">人气</option>
                <option value="newest">最新</option>
              </select>
            </div>
          </div>

          {/* 筛选选项 */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 科目筛选 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    课程科目
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="">所有科目</option>
                    {subjects.map(subject => (
                      <option key={subject} value={subject}>{subject}</option>
                    ))}
                  </select>
                </div>

                {/* 清除筛选 */}
                <div className="flex items-end">
                  <button
                    onClick={clearFilters}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    清除筛选
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 结果统计 */}
        <div className="mb-4 sm:mb-6 flex flex-col xs:flex-row xs:items-center xs:justify-between gap-1">
          <p className="text-sm text-gray-600">
            找到 <span className="font-medium">{filteredCourses.length}</span> 门课程
          </p>
          {searchTerm && (
            <p className="text-xs sm:text-sm text-blue-600 truncate">
              搜索: "{searchTerm}"
            </p>
          )}
        </div>

        {/* 课程列表 */}
        {filteredCourses.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredCourses.map((course) => {
              const isNew = isNewCourse(course)
              const isPopular = isPopularCourse(course)
              const hasDiscount = isDiscountCourse(course)
              
              return (
                <div 
                  key={course.id} 
                  className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-200 hover:-translate-y-1"
                >
                  <div className="relative aspect-w-16 aspect-h-9 bg-gradient-to-br from-blue-500 to-purple-600">
                    <img
                      className="h-48 w-full object-cover"
                      src={`https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=300&fit=crop&auto=format`}
                      alt={course.title}
                    />
                    
                    {/* 标签区域 */}
                    <div className="absolute top-3 left-3 flex flex-wrap gap-2">
                      {isNew && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                          <SparklesIcon className="w-3 h-3" />
                          新课程
                        </span>
                      )}
                      {isPopular && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                          <FireSolidIcon className="w-3 h-3" />
                          热门
                        </span>
                      )}
                      {hasDiscount && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                          优惠价
                        </span>
                      )}
                    </div>
                    
                    <div className="absolute top-3 right-3">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/90 text-gray-800">
                        {course.subject}
                      </span>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {course.teacher?.title || '讲师'}
                      </span>
                      <div className="flex items-center">
                        <StarSolidIcon className="h-4 w-4 text-yellow-400" />
                        <span className="ml-1 text-sm font-medium text-gray-600">
                          {(parseFloat(course.teacher?.rating) || 0).toFixed(1)}
                        </span>
                      </div>
                    </div>
                    
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                      {course.title}
                    </h3>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {course.description}
                    </p>
                    
                    <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <AcademicCapIcon className="w-4 h-4" />
                        <span>{course.teacher?.user?.firstName} {course.teacher?.user?.lastName}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <ClockIcon className="w-4 h-4" />
                          <span>{course.duration}小时</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <UserGroupIcon className="w-4 h-4" />
                          <span>{course._count?.bookings || 0}人</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        {hasDiscount ? (
                          <div>
                            <div className="text-2xl font-bold text-red-600">
                              ¥{course.price}
                            </div>
                            <div className="text-sm text-gray-500 line-through">
                              原价: ¥{course.finalPricePerHour}
                            </div>
                          </div>
                        ) : (
                          <div className="text-2xl font-bold text-gray-900">
                            ¥{course.finalPricePerHour || course.price}
                          </div>
                        )}
                      </div>
                      <Link
                        to={`/courses/${course.id}`}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      >
                        查看详情
                      </Link>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg shadow-md border border-gray-200">
            <BookOpenIcon className="mx-auto h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">没有找到符合条件的课程</h3>
            <p className="text-gray-500 mb-6">
              {searchTerm ? '尝试更改搜索关键词' : '尝试调整筛选条件'}
            </p>
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              清除筛选条件
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default CoursesPage 